import React from 'react'
import { List, ResponsiveTable } from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import { useUser } from '@fs/zion-user'
import { type GroupSummary } from '@fs/zion-tree-types'
import { type GedcomFileFailed, type GedcomFile } from '../../../services/groupTypes'
import { ManageTreeRow, ManageTreeRowSkeleton } from './ManageTreeRow'
import { useAllTreeGroups } from '../cets/AllTreeGroupsProvider'

// Combine processing files and groups into a single sorted list
type ListItem = { type: 'processing'; data: GedcomFile } | { type: 'group'; data: GroupSummary }

type Props = {
  /** Names the table for screen readers. Left off, the table announces only as "table, N columns". */
  caption?: string
  processingFiles?: GedcomFile[]
  groups: GroupSummary[]
  failedFiles?: GedcomFileFailed[]
  isMobile: boolean
  isFamilyGroupTree?: boolean
  showCombinedDesign: boolean
}

/** The single collation rule for every tree list on this page, so the orderings cannot drift apart. */
function compareNames(a: string, b: string): number {
  return a.toUpperCase().localeCompare(b.toUpperCase())
}

/** A processing file and a group hold their display name under different keys. */
function getItemName(item: ListItem): string {
  if (item.type === 'processing') return item.data.title
  return item.data.name
}

function byName(a: ListItem, b: ListItem): number {
  return compareNames(getItemName(a), getItemName(b))
}

type TableColumns = {
  columnWidths: number[]
  labels: string[]
}

/**
 * The table's shape, defined once so the loaded table and its skeleton cannot drift apart.
 *
 * A family group tree has no change log to report a modified date from, so it drops that column
 * entirely. The name column absorbs the freed width, which keeps Role and Tools in the same place
 * as the researcher tree table above it.
 */
function getTableColumns(t: (key: string) => string, isFamilyGroupTree?: boolean): TableColumns {
  const labels = [
    isFamilyGroupTree ? t('group-management.name.label') : t('tree-import.manage-trees.table-tree-name-heading'),
  ]
  let columnWidths = [8, 2, 2]

  if (!isFamilyGroupTree) {
    labels.push(t('tree-import.manage-trees.table-date-heading'))
    columnWidths = [6, 2, 2, 2]
  }

  labels.push(t('tree-import.manage-trees.table-role-heading'), t('tree-import.manage-trees.table-tools-heading'))

  return { columnWidths, labels }
}

/** The columned layout is desktop-only; at mobile width the trees stack as list items instead. */
function showsTableLayout(showCombinedDesign: boolean | undefined, isMobile: boolean): boolean {
  return Boolean(showCombinedDesign) && !isMobile
}

/** Renders a set of trees, choosing the wrapper element and telling each row which shape to take. */

export default function ManageTreeList({
  caption,
  processingFiles,
  groups,
  failedFiles,
  isMobile,
  isFamilyGroupTree,
  showCombinedDesign,
}: Props): JSX.Element | null {
  const [t] = useTranslation()
  const { uploadedFiles, loadingUploadedFiles } = useAllTreeGroups()
  const { cisId: currentUserCisId } = useUser()

  const showTableLayout = showsTableLayout(showCombinedDesign, isMobile)

  if (loadingUploadedFiles) {
    return (
      <ManageTreeListSkeleton
        isFamilyGroupTree={isFamilyGroupTree}
        isMobile={isMobile}
        showCombinedDesign={showCombinedDesign}
      />
    )
  }

  function renderTreeRow(group: GroupSummary, isLast: boolean): JSX.Element {
    const file = uploadedFiles.find((f) => f.treeId === group.treeIds?.[0])
    return (
      <ManageTreeRow
        key={group.id || file?.fileName || group.name}
        asTableRow={showTableLayout}
        isFamilyGroupTree={isFamilyGroupTree}
        showCombinedDesign={showCombinedDesign}
        group={group}
        file={file}
        isMobile={isMobile}
        isLast={isLast}
        currentUserCisId={currentUserCisId}
      />
    )
  }

  function renderProcessingRow(file: GedcomFile, isLast: boolean): JSX.Element {
    return (
      <ManageTreeRow
        key={file.fileName}
        asTableRow={showTableLayout}
        isFamilyGroupTree={isFamilyGroupTree}
        showCombinedDesign={showCombinedDesign}
        processingFile={file}
        isMobile={isMobile}
        isLast={isLast}
      />
    )
  }

  function renderFailedRow(file: GedcomFileFailed, isLast: boolean): JSX.Element {
    return (
      <ManageTreeRow
        key={file.fileName}
        asTableRow={showTableLayout}
        isFamilyGroupTree={isFamilyGroupTree}
        showCombinedDesign={showCombinedDesign}
        failedFile={file}
        isMobile={isMobile}
        isLast={isLast}
      />
    )
  }

  const combinedItems: ListItem[] = [
    ...(processingFiles ?? []).map((file) => ({ type: 'processing' as const, data: file })),
    ...groups.map((group) => ({ type: 'group' as const, data: group })),
  ].sort(byName)

  const failed = failedFiles ?? []
  const rows: JSX.Element[] = [
    ...combinedItems.map((item, index) => {
      const isLast = index === combinedItems.length - 1 && failed.length === 0
      if (item.type === 'processing') return renderProcessingRow(item.data, isLast)
      return renderTreeRow(item.data, isLast)
    }),
    ...failed.map((file, index) => renderFailedRow(file, index === failed.length - 1)),
  ]

  if (!showTableLayout) {
    return <List>{rows}</List>
  }

  if (rows.length === 0) {
    return null
  }

  const { columnWidths, labels } = getTableColumns(t, isFamilyGroupTree)

  return (
    <ResponsiveTable
      verticalAlign="middle"
      stickyHeader={false}
      caption={caption}
      totalColumnCount={labels.length}
      atSizeColumns={{ default: { labels, columnWidths } }}
      rowLevelHeaders
    >
      {rows}
    </ResponsiveTable>
  )
}

/** Stable keys for the placeholder rows; the count sets how tall the skeleton stands. */
const SKELETON_ROW_KEYS = ['first', 'second', 'third', 'fourth']

type SkeletonProps = {
  isFamilyGroupTree?: boolean
  isMobile: boolean
  showCombinedDesign?: boolean
}

/**
 * Stands in while the trees load, taking the same shape the loaded list will, so the card does not
 * reflow the moment the data arrives. The column headers are the real translated labels: they are
 * known before the data is, and skeletonizing them would only make them flash in later.
 */
export function ManageTreeListSkeleton({
  isFamilyGroupTree,
  isMobile,
  showCombinedDesign,
}: SkeletonProps): JSX.Element {
  const [t] = useTranslation()
  const showTableLayout = showsTableLayout(showCombinedDesign, isMobile)

  const rows = SKELETON_ROW_KEYS.map((key, index) => (
    <ManageTreeRowSkeleton
      key={key}
      asTableRow={showTableLayout}
      isFamilyGroupTree={isFamilyGroupTree}
      isLast={index === SKELETON_ROW_KEYS.length - 1}
      isMobile={isMobile}
      showCombinedDesign={showCombinedDesign}
    />
  ))

  if (!showTableLayout) {
    return <List>{rows}</List>
  }

  const { columnWidths, labels } = getTableColumns(t, isFamilyGroupTree)

  return (
    <ResponsiveTable
      verticalAlign="middle"
      stickyHeader={false}
      totalColumnCount={labels.length}
      atSizeColumns={{ default: { labels, columnWidths } }}
    >
      {rows}
    </ResponsiveTable>
  )
}
